// Comments on the pull request's description: conversation comments whose
// body quotes the passage they are about, so GitHub shows them readably,
// and end with a hidden marker that changelist reads back to put the
// comment beside that passage:
//
//   **On the description** · [view](https://github.com/o/r/pull/12)
//   > the quoted passage
//
//   <user's text>
//
//   <!-- changelist:description {"v":1,"quote":"the quoted passage","n":0} -->
//
// The marker keeps the passage as the description's text has it (spaces
// collapsed), and which of its occurrences it is (`n`, from 0).
import { escapeMarkdown } from './offdiff';

export interface DescMarker {
  v: 1;
  /** The passage, its whitespace collapsed to single spaces. */
  quote: string;
  /** Which occurrence of `quote` in the description's text, from 0. */
  n: number;
}

export interface DescNoteBody {
  marker: DescMarker;
  /** The user's text, without header and marker. */
  text: string;
}

/** Longest passage the marker keeps, in UTF-16 code units: a longer selection is kept by its start. */
export const PASSAGE_MAX = 600;
/** Longest passage the visible quote shows before it is cut with "…". */
const QUOTE_SHOWN = 280;
const MARKER_OPEN = '<!-- changelist:description ';

/** Text as the marker compares it: runs of whitespace as one space, trimmed. */
export const collapse = (s: string) => s.replace(/\s+/g, ' ').trim();

/** The marker comment; its JSON never contains `--` or `>`, so nothing in the passage can end it. */
export function encodeDescMarker(m: DescMarker): string {
  const json = JSON.stringify({ v: 1, quote: m.quote, n: m.n }).replace(/-/g, '\\u002d').replace(/>/g, '\\u003e');
  return `${MARKER_OPEN}${json} -->`;
}

/** A well-formed marker, or null. */
export function normalizeDescMarker(raw: unknown): DescMarker | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (o.v !== 1 || typeof o.quote !== 'string' || typeof o.n !== 'number') return null;
  const quote = collapse(o.quote);
  if (!quote || quote.length > PASSAGE_MAX || !Number.isInteger(o.n) || o.n < 0 || o.n > 10_000) return null;
  return { v: 1, quote, n: o.n };
}

/** The full comment body: header, quote, the user's text, marker. */
export function buildDescNoteBody(input: { marker: DescMarker; text: string; prUrl: string }): string {
  const m = normalizeDescMarker(input.marker);
  if (!m) throw new Error('invalid passage');
  const shown = m.quote.length > QUOTE_SHOWN ? m.quote.slice(0, QUOTE_SHOWN) + '…' : m.quote;
  const text = input.text.replace(/\r\n?/g, '\n').trim();
  return `**On the description** · [view](${input.prUrl})\n> ${escapeMarkdown(shown)}\n\n${text}\n\n${encodeDescMarker(m)}`;
}

const HEADER = /^\*\*On the description\*\* · \[view\]\([^\s)]*\)$/;

/** Split a body that ends with a description marker; null for any other body (or a malformed marker). */
export function parseDescNote(body: string | null | undefined): DescNoteBody | null {
  if (!body) return null;
  const s = body.replace(/\r\n?/g, '\n');
  const at = s.lastIndexOf(MARKER_OPEN);
  if (at < 0) return null;
  const tail = /^<!-- changelist:description (\{[^\n]*\}) -->\s*$/.exec(s.slice(at));
  if (!tail) return null;
  let marker: DescMarker | null;
  try {
    marker = normalizeDescMarker(JSON.parse(tail[1]));
  } catch {
    return null;
  }
  if (!marker) return null;
  const lines = s.slice(0, at).split('\n');
  let i = 0;
  if (HEADER.test(lines[0] ?? '')) {
    i = 1;
    while (i < lines.length && /^>( |$)/.test(lines[i])) i++;
  }
  return { marker, text: lines.slice(i).join('\n').trim() };
}


// ---------------------------------------------------------------- passages

const WORD = /[\p{L}\p{N}_]/u;

/**
 * The marker for the passage at `start`–`end` of the description's text
 * (collapsed): its words (a word it starts or ends inside, whole), cut at
 * PASSAGE_MAX, and how many times they occur before it. Null when the
 * passage is only whitespace.
 */
export function markerFor(text: string, start: number, end: number): DescMarker | null {
  while (start > 0 && start < end && WORD.test(text[start - 1]) && WORD.test(text[start])) start--;
  while (end < text.length && end > start && WORD.test(text[end - 1]) && WORD.test(text[end])) end++;
  const raw = text.slice(start, end);
  const from = start + (raw.length - raw.trimStart().length);
  const quote = raw.trim().slice(0, PASSAGE_MAX).trim();
  if (!quote) return null;
  let n = 0;
  for (let i = text.indexOf(quote); i >= 0 && i < from; i = text.indexOf(quote, i + 1)) n++;
  return { v: 1, quote, n };
}

/**
 * Where a marker's passage is in the description's text (collapsed):
 * its `n`th occurrence, else (the description was edited) its first;
 * null when the passage is no longer there.
 */
export function findPassage(text: string, m: DescMarker): { start: number; end: number } | null {
  const hits: number[] = [];
  for (let i = text.indexOf(m.quote); i >= 0; i = text.indexOf(m.quote, i + 1)) hits.push(i);
  const at = hits[m.n] ?? hits[0];
  return at === undefined ? null : { start: at, end: at + m.quote.length };
}

/** A position in the DOM: a text node and an offset in it. */
export interface TextPoint {
  node: Text;
  offset: number;
}

/**
 * The text under `root` as `collapse` has it (whitespace runs as one
 * space, trimmed), with the way between its offsets and the DOM:
 * `point(i)` is where its `i`th character is (or, at its end, the end of
 * the last text node), and `offset(node, o)` is how many of its
 * characters come before that DOM position.
 */
export function textIndex(root: Node): { text: string; point: (i: number) => TextPoint | null; offset: (node: Node, o: number) => number | null } {
  const out: string[] = [];
  const from: TextPoint[] = [];
  const starts = new Map<Text, Int32Array>();
  const walker = (root.ownerDocument ?? document).createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  let last: Text | null = null;
  for (let t = walker.nextNode() as Text | null; t; t = walker.nextNode() as Text | null) {
    const data = t.data;
    const before = new Int32Array(data.length + 1);
    for (let k = 0; k < data.length; k++) {
      before[k] = out.length;
      const space = /\s/.test(data[k]);
      if (space && (out.length === 0 || out[out.length - 1] === ' ')) continue;
      out.push(space ? ' ' : data[k]);
      from.push({ node: t, offset: k });
    }
    before[data.length] = out.length;
    starts.set(t, before);
    last = t;
  }
  if (out[out.length - 1] === ' ') {
    out.pop();
    from.pop();
  }
  const n = out.length;
  return {
    text: out.join(''),
    point: (i) => (i < n ? from[i] : last ? { node: last, offset: last.data.length } : null),
    offset: (node, o) => {
      if (node.nodeType !== 3) {
        // A position between children: before the first text at or after it.
        const child = node.childNodes[o] ?? null;
        const w = (root.ownerDocument ?? document).createTreeWalker(root, 4);
        if (child) {
          w.currentNode = child;
          const t = child.nodeType === 3 ? (child as Text) : (w.nextNode() as Text | null);
          return t && starts.has(t) ? Math.min(starts.get(t)![0], n) : n;
        }
        let next: Node | null = null;
        for (let p: Node | null = node; p && p !== root && !next; p = p.parentNode) next = p.nextSibling;
        if (!next) return n;
        w.currentNode = next;
        const t = next.nodeType === 3 ? (next as Text) : (w.nextNode() as Text | null);
        return t && starts.has(t) ? Math.min(starts.get(t)![0], n) : n;
      }
      const s = starts.get(node as Text);
      return s ? Math.min(s[Math.min(o, s.length - 1)], n) : null;
    },
  };
}
